import { useState } from 'react';
import { motion } from 'framer-motion';
import { LucideIcon, Lock, ArrowRight, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthDialog } from '@/components/auth/AuthDialog';
import fashionImage from '@/assets/empty-state-fashion.jpg';
import tryonImage from '@/assets/empty-state-tryon.jpg';
import chatImage from '@/assets/empty-state-chat.jpg';
import pollsImage from '@/assets/empty-state-polls.jpg';
import studioImage from '@/assets/empty-state-style-profile.jpg';

interface FeatureLockedStateProps {
  featureName: string;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  ctaLabel?: string;
  backgroundType?: 'fashion' | 'tryon' | 'chat' | 'polls' | 'studio';
  /** Show sign-in state instead of "save to unlock" state */
  signedOut?: boolean;
}

export function FeatureLockedState({
  featureName,
  icon: Icon,
  title,
  subtitle,
  ctaLabel,
  backgroundType = 'fashion',
  signedOut = false,
}: FeatureLockedStateProps) {
  const navigate = useNavigate();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  
  const getBackgroundImage = () => {
    switch (backgroundType) {
      case 'tryon': return tryonImage;
      case 'chat': return chatImage;
      case 'polls': return pollsImage;
      case 'studio': return studioImage;
      default: return fashionImage;
    }
  };
  const bgImage = getBackgroundImage();

  const handleGoToSaves = () => {
    navigate('/app');
  };

  const handleSignIn = () => {
    setShowAuthDialog(true);
  };

  const defaultCtaLabel = signedOut ? 'Sign in to continue' : 'Save something to unlock';
  const finalCtaLabel = ctaLabel || defaultCtaLabel;

  return (
    <div className="fixed inset-0 z-0 overflow-hidden">
      {/* Full-bleed image background */}
      <img
        src={bgImage}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: '50% 35%' }}
      />
      
      {/* Gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
      
      {/* Content positioned at bottom */}
      <div className="absolute inset-x-0 bottom-0 px-6 flex flex-col items-center" style={{ paddingBottom: 'calc(7rem + env(safe-area-inset-bottom))' }}>
        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-white mb-2 drop-shadow-lg text-center"
        >
          {title}
        </motion.h2>
        
        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-white/80 mb-6 drop-shadow text-center max-w-xs"
        >
          {subtitle}
        </motion.p>

        {/* CTA button */}
        <motion.button
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 25 }}
          onClick={signedOut ? handleSignIn : handleGoToSaves}
          className="flex items-center gap-3 px-6 py-3 rounded-full bg-white text-black font-medium shadow-xl active:scale-95 transition-transform"
          whileTap={{ scale: 0.95 }}
        >
          <span>{finalCtaLabel}</span>
          {signedOut ? <LogIn className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
        </motion.button>

        {/* Hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-sm text-white/60 mt-4 drop-shadow text-center"
        >
          {signedOut 
            ? 'Create a free account to get started' 
            : 'Save a link, photo, or product to unlock ✨'
          }
        </motion.p>
      </div>

      {/* Auth Dialog for signed out state */}
      {signedOut && (
        <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />
      )}
    </div>
  );
}
